import {
    mat3,
    quat,
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    vec3,
} from 'gl-matrix';

/**
 * The patient-aligned frame: no oblique rotation applied. A frame rotation
 * equal to this means each view sits on its standard anatomical plane.
 *
 * This is a shared singleton, not a frozen value — `Object.freeze` throws
 * on a non-empty `Float32Array`, so it cannot be made immutable at
 * runtime. The `ReadonlyMat3` type prevents in-place edits through this
 * binding at compile time; callers must never cast it back to `mat3` or
 * pass it as an `out` target to a gl-matrix mutator.
 */
export const IDENTITY_ROTATION: ReadonlyMat3 = mat3.create();

/**
 * Angular tolerance (degrees) below which a rotation is treated as
 * patient-aligned. Shared by every consumer that decides whether a frame
 * or a view plane sits on its standard orientation, so no rotation can be
 * blocked as oblique by one check and accepted as aligned by another.
 */
export const FRAME_ALIGNMENT_TOLERANCE_DEG = 0.05;

const DEG_PER_RAD = 180 / Math.PI;

/**
 * Two unit directions count as parallel when the absolute value of their dot
 * product is at least this: the cosine of `FRAME_ALIGNMENT_TOLERANCE_DEG`.
 */
const PARALLEL_DIRECTIONS_MIN_COS = Math.cos(
    FRAME_ALIGNMENT_TOLERANCE_DEG / DEG_PER_RAD
);

/**
 * Whether two unit directions are parallel within the alignment tolerance.
 * Opposite directions count as parallel: as plane normals, both describe the
 * same plane.
 */
export function areParallelDirections(
    a: ReadonlyVec3,
    b: ReadonlyVec3
): boolean {
    return Math.abs(vec3.dot(a, b)) >= PARALLEL_DIRECTIONS_MIN_COS;
}

/**
 * Below this distance (canvas px) from the pivot, a press does not start a
 * rotation: the angular mapping is degenerate at the centre (an
 * infinitesimal pointer move there sweeps an arbitrarily large bearing).
 */
export const ROTATE_DEAD_ZONE_PX = 12;

/**
 * The signed angular change of a point's bearing about `pivotPx`, moving
 * from `fromPx` to `toPx`, in canvas pixel space (radians).
 *
 * This is the "grab and spin" primitive behind {@link RotateViewTool}: the
 * pivot is the plane intersection point projected to canvas coordinates,
 * and the returned angle is how far the cursor has swept around it, so the
 * anatomy under the cursor can be made to track it 1:1 rather than moving
 * at a fixed degrees-per-pixel rate.
 *
 * Canvas Y grows downward, so `Math.atan2(dy, dx)` here does not match the
 * textbook (Y-up) CCW-positive convention — it is whatever this flipped
 * frame reports. That is deliberate: {@link rotationAboutViewNormal} is
 * defined to match it, and together they make a screen-space drag angle
 * map onto a rotation that visually tracks the cursor. Do not compare this
 * value against a Y-up angle without re-deriving the sign.
 *
 * A pure function of the three points, not an accumulator, so it can
 * always be computed fresh from the press position — that is what keeps
 * `RotateViewTool` deterministic regardless of how many intermediate move
 * events fire (see its class doc).
 *
 * The result is wrapped to (-π, π], so a sweep of more than half a turn in
 * one call reports the shorter way around rather than the literal bearing
 * delta — a +181° sweep is reported as -179°.
 *
 * That is harmless, but NOT for the reason an earlier version of this
 * comment claimed. It asserted that a drag could not reach the ambiguity
 * without the pointer passing through the pivot; that is simply false — the
 * pointer can circle the pivot indefinitely without ever crossing it. The
 * real reason is that rotations are 2π-periodic: the consumer turns this
 * angle into a rotation matrix, and R(+181°) and R(-179°) are the same
 * matrix (verified equal to ~2e-16). A sweep through the wrap point is
 * therefore continuous in the only quantity that reaches the view, and the
 * anatomy neither jumps nor reverses.
 *
 * Consequently the bearing must NOT be unrolled across move events to
 * "fix" the wrap: that would buy nothing visually and would reintroduce the
 * accumulator this function exists to avoid, breaking the property that the
 * angle is a pure function of press and current position.
 *
 * Degenerate at the pivot: callers must keep `fromPx` and `toPx` clear of
 * `pivotPx` (see {@link ROTATE_DEAD_ZONE_PX}); this function
 * does not guard against it.
 */
export function signedAngleAboutPivotRad(
    fromPx: ReadonlyVec2,
    toPx: ReadonlyVec2,
    pivotPx: ReadonlyVec2
): number {
    const fromBearingRad = Math.atan2(
        fromPx[1] - pivotPx[1],
        fromPx[0] - pivotPx[0]
    );
    const toBearingRad = Math.atan2(toPx[1] - pivotPx[1], toPx[0] - pivotPx[0]);
    return wrapToSignedPi(toBearingRad - fromBearingRad);
}

/**
 * A rotation of `angleRad` about `viewNormal` (a PatientSpace axis),
 * oriented so that a positive `angleRad` — as produced by
 * {@link signedAngleAboutPivotRad} from a screen-space drag — sweeps the
 * anatomy in the same visual direction on screen as the drag, once this
 * delta is applied the way the app actually applies it: forward, onto the
 * dragged **view's own** rotation matrix (`composeFrameRotation` computes
 * `delta * current`, and `PlaneView.applyFrameRotation` orients the view
 * by that composed value) — never directly onto a
 * PatientSpace point held under a fixed view.
 *
 * That distinction is the whole sign convention, and it is easy to get
 * backwards: rotating the *view* forward by `Rodrigues(viewNormal, +θ)`
 * sweeps a fixed anatomical point's on-screen bearing by `+θ` (same
 * direction), because rotating the view is equivalent, from the screen's
 * perspective, to rotating the *content* by the inverse `-θ` under a
 * fixed view — the classic camera-vs-world duality. So no extra sign
 * flip is needed here: `angleRad` is passed straight through to the
 * (already-correct, right-hand-rule) Rodrigues rotation. An earlier
 * version of this function negated `angleRad` on the theory that a
 * right-handed (viewX, viewY, viewNormal) frame plus a canvas-Y-down
 * screen mapping required it — that reasoning only holds if the
 * *content* rotates under a fixed view, which is not what this app does,
 * and it shipped an inverted rotation (caught by the domain expert
 * dragging the tool the wrong way in the running app).
 *
 * This sign is pinned by the acceptance test in frameRotation.test.ts
 * ("anatomy follows the cursor"): it builds a small `PlaneView` harness,
 * rotates the harness's own rotation matrix forward by this function's
 * output (mirroring `composeFrameRotation` +
 * `PlaneView.applyFrameRotation` exactly), and asserts a *fixed*
 * PatientSpace point's on-screen bearing — read through PlaneView's real,
 * unmodified `canvasCoordsFromPatientCoords` — moves by the drag angle in
 * the drag's own direction. A mirrored sign passes every
 * orthonormality/magnitude check but fails that test — this is the
 * second sign-convention defect on this branch; the first (a
 * transposition in the Euler decomposition) was caught only by a
 * reviewer hand-tracing the numbers, and this one only by manual testing
 * in the running app, which is exactly why the acceptance test now
 * exercises the real projection path instead of a hand-rolled one.
 */
export function rotationAboutViewNormal(
    angleRad: number,
    viewNormal: ReadonlyVec3
): mat3 {
    return rotationAboutAxis(viewNormal, angleRad);
}

/**
 * Apply `delta` on top of `current`, returning a new matrix. Neither input
 * is mutated.
 *
 * The result is re-orthonormalised: composing hundreds of small rotations
 * accumulates floating-point error, and a drifted matrix would shear the
 * rendered image rather than rotate it.
 *
 * A result that lands within the alignment tolerance of identity is snapped
 * to EXACT identity. `isPatientAligned` is read by two different consumers
 * asking two different questions — the indicator asking "is this worth
 * telling the user about?" and the contouring gate asking "is it safe to
 * author a contour as though this plane were anatomical?" — and a frame
 * sitting just inside the tolerance would answer them inconsistently:
 * no OBLIQUE badge shown, yet the plane genuinely tilted and contouring
 * permitted. Snapping collapses that gap, so for any frame this function
 * produces, "not flagged as oblique" and "exactly patient-aligned" are the
 * same state rather than merely close ones.
 */
export function composeFrameRotation(
    current: ReadonlyMat3,
    delta: ReadonlyMat3
): mat3 {
    const composed = mat3.multiply(mat3.create(), delta, current);
    const orthonormal = orthonormalise(composed);
    return isPatientAligned(orthonormal) ? mat3.create() : orthonormal;
}

/**
 * True when the frame is (within tolerance) the patient-aligned frame, i.e.
 * the three views sit on their standard anatomical planes.
 *
 * Measured as the rotation angle of the matrix, so it is independent of how
 * the rotation decomposes into axes.
 */
export function isPatientAligned(
    rotation: ReadonlyMat3,
    toleranceDeg: number = FRAME_ALIGNMENT_TOLERANCE_DEG
): boolean {
    return Math.abs(rotationAngleDeg(rotation)) <= toleranceDeg;
}

/**
 * The smallest rotation that turns `fromNormal` onto `toNormal`. The sign of
 * the normals matters: an opposite normal is the same plane seen from the
 * other side, and the rotation turns all the way to that side. Returns the
 * exact identity when the two are already parallel within the alignment
 * tolerance.
 *
 * @throws if either normal has zero length.
 */
export function rotationBetweenNormals(
    fromNormal: ReadonlyVec3,
    toNormal: ReadonlyVec3
): mat3 {
    if (!(vec3.length(fromNormal) > 0 && vec3.length(toNormal) > 0)) {
        throw new Error(
            'rotationBetweenNormals: a normal must have non-zero length'
        );
    }
    const from = vec3.normalize(vec3.create(), fromNormal);
    const to = vec3.normalize(vec3.create(), toNormal);
    const rotation = mat3.fromQuat(
        mat3.create(),
        quat.rotationTo(quat.create(), from, to)
    );
    return isPatientAligned(rotation) ? mat3.create() : rotation;
}

/**
 * Decompose the frame rotation into angles about the patient axes, for
 * display. Fixed ZYX (intrinsic) order so the readout is deterministic.
 *
 *  - lrDeg: rotation about the patient left-right axis (X)
 *  - apDeg: rotation about the patient anterior-posterior axis (Y)
 *  - siDeg: rotation about the patient superior-inferior axis (Z)
 */
export function obliquityAnglesDeg(rotation: ReadonlyMat3): {
    lrDeg: number;
    apDeg: number;
    siDeg: number;
} {
    // gl-matrix mat3 is column-major: m[col * 3 + row]. Intrinsic ZYX
    // decomposition (R = Rz(si)*Ry(ap)*Rx(lr)) needs row-2 elements R20/R21
    // for the ap/lr split, not the column-2 elements R02/R12 — mind the
    // transpose.
    const m00 = rotation[0];
    const m20 = rotation[2];
    const m21 = rotation[5];
    const m22 = rotation[8];

    const apRad = Math.asin(-clamp(m20, -1, 1));
    const cosAp = Math.cos(apRad);

    // Near gimbal lock the LR/SI split is ill-conditioned; fold the whole
    // rotation onto SI so the readout stays finite and reproducible.
    //
    // Read R01/R11, NOT R10/R11. At ap = +-90 the R10 term collapses to
    // zero for Rz(si) * Ry(+-90) * Rx(lr), so keying off it silently
    // reports 0 and throws away the coupled rotation entirely -- for
    // si = 40, ap = 90, lr = 15 the fold is si - lr = 25, and the R10 form
    // returns 0. The leading minus is required: atan2(R01, R11) has the
    // right magnitude but the wrong sign. Verified by reconstruction --
    // Rz(25) * Ry(90) reproduces that matrix and Rz(-25) * Ry(90) does not.
    if (Math.abs(cosAp) < 1e-6) {
        return {
            lrDeg: 0,
            apDeg: apRad * DEG_PER_RAD,
            siDeg: Math.atan2(-rotation[3], rotation[4]) * DEG_PER_RAD,
        };
    }

    return {
        lrDeg: Math.atan2(m21, m22) * DEG_PER_RAD,
        apDeg: apRad * DEG_PER_RAD,
        siDeg: Math.atan2(rotation[1], m00) * DEG_PER_RAD,
    };
}

// ── private helpers ──────────────────────────────────────────

/** Rodrigues rotation about an arbitrary (unit-ised) axis. */
function rotationAboutAxis(axis: ReadonlyVec3, angleRad: number): mat3 {
    const unit = vec3.normalize(vec3.create(), axis as vec3);
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    const t = 1 - c;
    const [x, y, z] = unit;

    // Column-major.
    return mat3.fromValues(
        t * x * x + c,
        t * x * y + s * z,
        t * x * z - s * y,
        t * x * y - s * z,
        t * y * y + c,
        t * y * z + s * x,
        t * x * z + s * y,
        t * y * z - s * x,
        t * z * z + c
    );
}

/** Gram-Schmidt re-orthonormalisation, preserving the first column. */
function orthonormalise(m: mat3): mat3 {
    const c0 = vec3.fromValues(m[0], m[1], m[2]);
    const c1 = vec3.fromValues(m[3], m[4], m[5]);

    vec3.normalize(c0, c0);
    const proj = vec3.scale(vec3.create(), c0, vec3.dot(c1, c0));
    vec3.subtract(c1, c1, proj);
    vec3.normalize(c1, c1);
    const c2 = vec3.cross(vec3.create(), c0, c1);

    return mat3.fromValues(
        c0[0],
        c0[1],
        c0[2],
        c1[0],
        c1[1],
        c1[2],
        c2[0],
        c2[1],
        c2[2]
    );
}

/** Overall rotation angle of the matrix, in degrees. */
function rotationAngleDeg(rotation: ReadonlyMat3): number {
    const trace = rotation[0] + rotation[4] + rotation[8];
    const cosAngle = clamp((trace - 1) / 2, -1, 1);
    return Math.acos(cosAngle) * DEG_PER_RAD;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/** Wrap an angle in radians to (-π, π]. */
function wrapToSignedPi(angleRad: number): number {
    const twoPi = 2 * Math.PI;
    let wrapped = angleRad % twoPi;
    if (wrapped > Math.PI) {
        wrapped -= twoPi;
    } else if (wrapped <= -Math.PI) {
        wrapped += twoPi;
    }
    return wrapped;
}
